/**
 * Regelversion 8: Steuerreform (nur auf „Schwer“) und Standortpaket.
 *
 * Beide Vorlagen hängen wie die Vorhaben aus Version 2 und 3 an eigenen Themen (ag8_…), die nur im
 * Nachschlagewerk ISS stehen. Sie werden über den Reiter „Haushalt“ geöffnet.
 *
 * Felder in fx, die nur Version 8 auswertet:
 *  - b8:   Belastung je Gruppe in Mrd €/Jahr, mit Vorzeichen (Entlastung negativ): rich, unt, arb
 *  - tax:  Steueränderung je Gruppe (Anzeige „Steuern je Gruppe“)
 *  - gini: Wirkung auf die soziale Schere (Gini-Punkte, negativ = kleiner)
 *  - lure: Wie stark das Gesetz Abgewanderte zurückholt (0–1 je Gruppe)
 */
import { ISS } from './states';

/** Steuerarten der Steuerreform: Mrd € pro Jahr, positiv = Erhöhung. */
export const TAX_GROUPS: { id: string; l: string; g: string; d: string }[] = [
  { id: 'arb', l: 'Lohn- und Einkommensteuer', g: 'arb', d: 'Arbeitnehmer mit normalem Einkommen' },
  { id: 'fam', l: 'Familien (Kinderfreibetrag, Splitting)', g: 'fam', d: 'Eltern und Ehepaare' },
  { id: 'rent', l: 'Besteuerung der Renten', g: 'rent', d: 'Rentnerinnen und Rentner' },
  { id: 'top', l: 'Spitzensteuersatz und Reichensteuer', g: 'rich', d: 'Spitzenverdiener und Vermögende' },
  { id: 'unt', l: 'Unternehmensteuern', g: 'unt', d: 'Körperschaft- und Gewerbesteuer' },
];
/** Neue Steuern, die es bisher nicht gibt. */
export const NEW_TAXES: { id: string; l: string; max: number; st: number; u: string; per: number; d: string }[] = [
  { id: 'vst', l: 'Neu: Vermögensteuer', max: 2, st: .5, u: ' %', per: 9, d: 'auf Vermögen über 2 Mio €' },
  { id: 'dst', l: 'Neu: Digitalsteuer', max: 5, st: 1, u: ' %', per: .8, d: 'auf Umsätze großer Tech-Konzerne' },
  { id: 'ftt', l: 'Neu: Finanztransaktionsteuer', max: .2, st: .05, u: ' %', per: 30, d: 'auf Aktien- und Derivatehandel' },
  { id: 'ueb', l: 'Neu: Übergewinnsteuer', max: 1, st: 1, u: '', per: 6, d: 'auf außergewöhnliche Krisengewinne von Konzernen' },
];

const sum = (o: Record<string, number>) => Object.values(o).reduce((s, x) => s + x, 0);

export const STEUER8 = {
  id: 'ag8_steuer', n: 'Steuern', d: 'Wer soll wie viel zum Haushalt beitragen?', sev: 0, rate: 0, imp: {}, agenda: true,
  laws: [{
    t: 'Steuerreform', r: 'Finanzen', br: 1,
    d: 'Ein Steuergesetz legt fest, wie viel jede Gruppe zahlt. Jede Erhöhung bringt Geld und Ärger, jede Senkung kostet Geld und schafft Freunde. Neue Steuern lassen sich hier einführen.',
    ps: [
      ...TAX_GROUPS.map((x) => ({ id: x.id, l: `${x.l} (${x.d})`, k: 'r', min: -12, max: 12, st: 1, u: ' Mrd €/J', d: 0 })),
      ...NEW_TAXES.map((x) => (x.id === 'ueb' ? { id: x.id, l: `${x.l} (${x.d})`, k: 'b', d: 0 } : { id: x.id, l: `${x.l} (${x.d})`, k: 'r', min: 0, max: x.max, st: x.st, u: x.u, d: 0 })),
    ],
    fx: (p: any) => {
      const n: Record<string, number> = {}; for (const x of NEW_TAXES) n[x.id] = (+p[x.id] || 0) * x.per;
      const t: Record<string, number> = {}; for (const x of TAX_GROUPS) t[x.id] = +p[x.id] || 0;
      const b = sum(t) + sum(n);
      const unt = t.unt + n.dst + n.ftt * .4 + n.ueb, rich = t.top + n.vst + n.ftt * .6;
      return {
        months: 3, b, sev: 0, cut: 0,
        g: { arb: -t.arb * .35 - n.ftt * .02, fam: -t.fam * .4, rent: -t.rent * .4, unt: -unt * .3 - Math.max(0, t.top) * .05, jung: -t.arb * .1 },
        gr: -unt * .006 - Math.max(0, rich) * .003 + Math.max(0, -t.arb - t.fam) * .002,
        pos: { steuern: rich * .09, buero: -unt * .04, sozial: (t.top - t.arb * .5 - t.fam * .5) * .03, haushalt: b * -.02 },
        b8: { rich, unt, arb: t.arb + t.fam * .5 + t.rent * .3 },
        tax: { ...t, ...n },
        gini: Math.max(-2.5, Math.min(2.5, -rich * .06 - n.ftt * .01 + t.arb * .01 - t.fam * .03 - t.rent * .02 + (t.unt < 0 ? -t.unt * .03 : -t.unt * .01))),
        tax8: true,
      };
    },
  }],
};

export const STANDORT8 = {
  id: 'ag8_standort', n: 'Standort', d: 'Wie holt Deutschland Abgewanderte zurück?', sev: 0, rate: 0, imp: {}, agenda: true,
  laws: [{
    t: 'Standortpaket', r: 'Wirtschaft', br: 1,
    d: 'Prämien und Steuerrabatte, damit Vermögende, Unternehmen und Fachkräfte zurückkommen oder bleiben. Wirkt gegen Abwanderung, kostet Geld und vergrößert die soziale Schere.',
    ps: [
      { id: 'rv', l: 'Rückkehrprämie für Vermögende (Steuerrabatt)', k: 'r', min: 0, max: 6, st: 1, u: ' Mrd €/J', d: 2 },
      { id: 'ru', l: 'Ansiedlungsförderung für Unternehmen', k: 'r', min: 0, max: 10, st: 1, u: ' Mrd €/J', d: 3 },
      { id: 'ra', l: 'Rückkehrprogramm für Fachkräfte', k: 'r', min: 0, max: 4, st: .5, u: ' Mrd €/J', d: 1 },
    ],
    fx: (p: any) => ({
      months: 3, b: -(p.rv + p.ru + p.ra), sev: 0, cut: 0,
      g: { unt: p.ru * .5 + p.rv * .1, arb: p.ra * .4 - p.rv * .15, fam: -p.rv * .1, jung: p.ra * .3 },
      gr: p.ru * .012 + p.ra * .01,
      pos: { steuern: -p.rv * .2, buero: p.ru * .08, sozial: -p.rv * .08 },
      b8: { rich: -p.rv * 1.5, unt: -p.ru * .8, arb: -p.ra * 1.5 },
      lure: { rich: Math.min(1, p.rv / 4), unt: Math.min(1, p.ru / 6), arb: Math.min(1, p.ra / 3) },
      gini: p.rv * .12 + p.ru * .03,
    }),
  }],
};

for (const I of [STEUER8, STANDORT8]) (ISS as any)[I.id] = I;
export const AG8_IDS = [STEUER8.id, STANDORT8.id];
